import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import "../App.css";

function ReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchReport();
  }, [id]);

  const fetchReport = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`/api/reports/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to fetch report");
      const data = await response.json();
      setReport(data.report);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`/api/reports/${id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `MarTech-Monitoring-${report.report_type}-${report.week_ending || "report"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download report: " + err.message);
    }
  };

  if (loading) return <div className="loading">Loading report...</div>;
  if (error) return (
    <div>
      <div className="content-header"><h1 style={{ margin: 0 }}>Error</h1></div>
      <div className="content-body">
        <div style={{ padding: "12px", background: "#fecaca", color: "#991b1b", borderRadius: "6px" }}>{error}</div>
      </div>
    </div>
  );

  const d = report.report_data;
  if (!d) return (
    <div>
      <div className="content-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1 style={{ margin: "0 0 5px 0" }}>{report.title}</h1>
          <p style={{ margin: 0, color: "#6b7280" }}>No detailed data available for this report.</p>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button className="btn btn-primary" onClick={handleDownload} style={{ fontSize: "0.85rem" }}>Download PDF</button>
          <button className="btn" onClick={() => navigate("/reports")} style={{ fontSize: "0.85rem" }}>Back to Reports</button>
        </div>
      </div>
    </div>
  );

  const scoreColor = d.health_score >= 80 ? "#38a169" : d.health_score >= 60 ? "#d69e2e" : "#e53e3e";
  const scoreLabel = d.health_score >= 80 ? "Healthy" : d.health_score >= 60 ? "Needs Attention" : "Critical";
  const scoreBg = d.health_score >= 80 ? "#dcfce7" : d.health_score >= 60 ? "#fef3c7" : "#fee2e2";

  const severityColor = (s) => {
    const map = { CRITICAL: "#e53e3e", HIGH: "#dd6b20", MEDIUM: "#d69e2e", LOW: "#38a169" };
    return map[s.toUpperCase()] || "#718096";
  };
  const severityBg = (s) => {
    const map = { CRITICAL: "#fee2e2", HIGH: "#ffedd5", MEDIUM: "#fef3c7", LOW: "#dcfce7" };
    return map[s.toUpperCase()] || "#f3f4f6";
  };
  const trendIcon = (t) => {
    if (t === "improving") return { symbol: "\u25B2", color: "#38a169", label: "Improving" };
    if (t === "declining") return { symbol: "\u25BC", color: "#e53e3e", label: "Declining" };
    return { symbol: "\u25B6", color: "#718096", label: "Stable" };
  };

  const categories = Object.keys(d.checks_by_category || {});
  const maxCheck = Math.max(...categories.map(c => d.checks_by_category[c]), 1);

  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const responseTimes = d.daily_response_times || [];
  const maxTime = Math.max(...responseTimes, 1);
  const avgTime = responseTimes.length > 0 ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length : 0;

  const statBox = (value, label) => (
    <div style={{
      flex: "1 1 0", textAlign: "center", padding: "16px 8px",
      background: darkMode ? "#1e293b" : "#f7fafc",
      borderRadius: "8px", border: darkMode ? "1px solid #334155" : "1px solid #e2e8f0"
    }}>
      <div style={{ fontSize: "1.5rem", fontWeight: 700, color: darkMode ? "#e2e8f0" : "#1a365d" }}>{value}</div>
      <div style={{ fontSize: "0.75rem", color: darkMode ? "#94a3b8" : "#718096", marginTop: "4px" }}>{label}</div>
    </div>
  );

  const pctChange = (curr, prev) => {
    if (!prev) return "";
    const change = ((curr - prev) / prev * 100).toFixed(1);
    return change > 0 ? `+${change}%` : `${change}%`;
  };

  return (
    <div>
      <div className="content-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1 style={{ margin: "0 0 5px 0" }}>{d.client_name} — Weekly Report</h1>
          <p style={{ margin: 0, color: "#6b7280" }}>Week ending {d.week_ending}</p>
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <button className="btn btn-primary" onClick={handleDownload} style={{ fontSize: "0.85rem" }}>Download PDF</button>
          <button className="btn" onClick={() => navigate("/reports")} style={{ fontSize: "0.85rem" }}>Back to Reports</button>
        </div>
      </div>

      <div className="content-body">

        {/* ── EXECUTIVE SUMMARY ── */}
        <div className="card">
          <div className="card-header">
            <h3>Executive Summary</h3>
            <div style={{
              display: "flex", alignItems: "center", gap: "10px",
              padding: "6px 16px", borderRadius: "20px",
              background: scoreBg, color: scoreColor, fontWeight: 700, fontSize: "0.9rem"
            }}>
              <span style={{ fontSize: "1.5rem" }}>{d.health_score}</span>
              <span style={{ fontSize: "0.75rem", fontWeight: 600 }}>{scoreLabel}</span>
            </div>
          </div>
          <div className="card-body">
            <p style={{ lineHeight: "1.7", margin: "0 0 20px 0", fontSize: "0.95rem" }}>{d.executive_narrative}</p>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              {statBox(d.total_checks.toLocaleString(), "Diagnostic Checks")}
              {statBox(d.issues_detected, "Issues Detected")}
              {statBox(`${d.avg_response_time} min`, "Avg Response Time")}
              {statBox(`${d.uptime_pct}%`, "Monitoring Uptime")}
            </div>
          </div>
        </div>

        {/* ── RISK DASHBOARD ── */}
        <div className="card">
          <div className="card-header">
            <h3>Risk Dashboard</h3>
            <span style={{ fontSize: "0.8rem", color: darkMode ? "#94a3b8" : "#6b7280" }}>Ranked by business impact</span>
          </div>
          <div className="card-body">
            {d.risks.map((risk, i) => {
              const trend = trendIcon(risk.trend);
              return (
                <div key={i} style={{
                  display: "flex", alignItems: "flex-start", gap: "14px",
                  padding: "14px 0",
                  borderBottom: i < d.risks.length - 1 ? (darkMode ? "1px solid #334155" : "1px solid #e2e8f0") : "none"
                }}>
                  <div style={{
                    minWidth: "28px", height: "28px", borderRadius: "50%",
                    background: darkMode ? "#334155" : "#f1f5f9",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 700, fontSize: "0.8rem", color: darkMode ? "#94a3b8" : "#64748b"
                  }}>{i + 1}</div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: "0 0 6px 0", fontSize: "0.9rem", lineHeight: "1.5" }}>{risk.description}</p>
                    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
                      <span style={{
                        padding: "2px 10px", borderRadius: "12px", fontSize: "0.7rem", fontWeight: 700,
                        background: severityBg(risk.severity), color: severityColor(risk.severity),
                        textTransform: "uppercase"
                      }}>{risk.severity}</span>
                      <span style={{ fontSize: "0.8rem", fontWeight: 600, color: darkMode ? "#cbd5e1" : "#475569" }}>
                        Est. Impact: {risk.impact}
                      </span>
                      <span style={{ fontSize: "0.8rem", color: trend.color }}>
                        {trend.symbol} {trend.label}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── MONITORING ACTIVITY ── */}
        <div className="card">
          <div className="card-header">
            <h3>Monitoring Activity</h3>
          </div>
          <div className="card-body">

            {/* Checks by Category */}
            <h4 style={{ margin: "0 0 12px 0", fontSize: "0.85rem", fontWeight: 600, color: darkMode ? "#94a3b8" : "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Checks by Category
            </h4>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "24px" }}>
              {categories.map((cat) => {
                const curr = d.checks_by_category[cat];
                const prev = d.checks_by_category_last_week?.[cat] || 0;
                const pct = (curr / maxCheck) * 100;
                return (
                  <div key={cat}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                      <span style={{ fontSize: "0.85rem", textTransform: "capitalize" }}>{cat.replace(/_/g, " ")}</span>
                      <span style={{ fontSize: "0.8rem", color: darkMode ? "#94a3b8" : "#6b7280" }}>
                        {curr.toLocaleString()} <span style={{ fontSize: "0.7rem" }}>({pctChange(curr, prev)} vs last week)</span>
                      </span>
                    </div>
                    <div style={{
                      height: "8px", borderRadius: "4px",
                      background: darkMode ? "#334155" : "#e2e8f0", overflow: "hidden"
                    }}>
                      <div style={{
                        width: `${pct}%`, height: "100%", borderRadius: "4px",
                        background: "#2b6cb0", transition: "width 0.5s"
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Issue Resolution */}
            <h4 style={{ margin: "0 0 12px 0", fontSize: "0.85rem", fontWeight: 600, color: darkMode ? "#94a3b8" : "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Issue Resolution
            </h4>
            <div style={{ display: "flex", gap: "12px", marginBottom: "24px", flexWrap: "wrap" }}>
              {[
                { label: "Detected", value: d.issues_detected, color: "#3182ce", bg: "#dbeafe" },
                { label: "Auto-Resolved", value: d.issues_auto_resolved, color: "#38a169", bg: "#dcfce7" },
                { label: "Escalated", value: d.issues_escalated, color: "#e53e3e", bg: "#fee2e2" },
              ].map((item) => (
                <div key={item.label} style={{
                  flex: "1 1 0", textAlign: "center", padding: "14px",
                  borderRadius: "8px", background: darkMode ? "#1e293b" : item.bg,
                  border: `2px solid ${item.color}22`
                }}>
                  <div style={{ fontSize: "1.5rem", fontWeight: 700, color: item.color }}>{item.value}</div>
                  <div style={{ fontSize: "0.75rem", color: darkMode ? "#94a3b8" : "#718096", marginTop: "4px" }}>{item.label}</div>
                </div>
              ))}
            </div>

            {/* Response Time */}
            <h4 style={{ margin: "0 0 12px 0", fontSize: "0.85rem", fontWeight: 600, color: darkMode ? "#94a3b8" : "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Response Time (Minutes)
            </h4>
            <div style={{
              display: "flex", alignItems: "flex-end", gap: "6px", height: "120px",
              padding: "0 0 24px 0", position: "relative",
              borderBottom: darkMode ? "1px solid #334155" : "1px solid #e2e8f0"
            }}>
              {/* Avg line */}
              <div style={{
                position: "absolute", left: 0, right: 0,
                bottom: `${(avgTime / maxTime) * 100 + 24}px`,
                borderTop: "2px dashed #d69e2e", opacity: 0.5
              }} />
              <div style={{
                position: "absolute", right: 0,
                bottom: `${(avgTime / maxTime) * 100 + 26}px`,
                fontSize: "0.65rem", color: "#d69e2e"
              }}>Avg: {avgTime.toFixed(1)}m</div>

              {responseTimes.map((t, i) => (
                <div key={i} style={{ flex: "1 1 0", display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <div style={{ fontSize: "0.65rem", marginBottom: "4px", color: darkMode ? "#94a3b8" : "#718096" }}>{t}m</div>
                  <div style={{
                    width: "100%", maxWidth: "40px",
                    height: `${(t / maxTime) * 96}px`,
                    background: t > avgTime * 1.5 ? "#e53e3e" : "#2b6cb0",
                    borderRadius: "4px 4px 0 0",
                    transition: "height 0.5s"
                  }} />
                  <div style={{ fontSize: "0.7rem", marginTop: "6px", color: darkMode ? "#94a3b8" : "#718096" }}>{days[i]}</div>
                </div>
              ))}
            </div>

            {/* Week-over-Week */}
            <h4 style={{ margin: "20px 0 12px 0", fontSize: "0.85rem", fontWeight: 600, color: darkMode ? "#94a3b8" : "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Week-over-Week Comparison
            </h4>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: darkMode ? "2px solid #334155" : "2px solid #e2e8f0" }}>
                  {["Metric", "This Week", "Last Week", "Change"].map((h, i) => (
                    <th key={i} style={{
                      textAlign: i === 0 ? "left" : "center", padding: "8px 12px",
                      fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase",
                      color: darkMode ? "#94a3b8" : "#64748b"
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { metric: "Total Checks", curr: d.total_checks.toLocaleString(), prev: d.total_checks_last_week.toLocaleString(), change: pctChange(d.total_checks, d.total_checks_last_week) },
                  { metric: "Issues Detected", curr: d.issues_detected, prev: d.issues_detected_last_week, change: `${d.issues_detected - d.issues_detected_last_week > 0 ? "+" : ""}${d.issues_detected - d.issues_detected_last_week}` },
                  { metric: "Avg Response Time", curr: `${d.avg_response_time} min`, prev: `${d.avg_response_time_last_week} min`, change: `${(d.avg_response_time - d.avg_response_time_last_week) > 0 ? "+" : ""}${(d.avg_response_time - d.avg_response_time_last_week).toFixed(1)} min` },
                  { metric: "Monitoring Uptime", curr: `${d.uptime_pct}%`, prev: `${d.uptime_pct_last_week}%`, change: "" },
                ].map((row, i) => (
                  <tr key={i} style={{ borderBottom: darkMode ? "1px solid #1e293b" : "1px solid #f1f5f9" }}>
                    <td style={{ padding: "10px 12px", fontWeight: 600, fontSize: "0.85rem" }}>{row.metric}</td>
                    <td style={{ padding: "10px 12px", textAlign: "center", fontSize: "0.85rem" }}>{row.curr}</td>
                    <td style={{ padding: "10px 12px", textAlign: "center", fontSize: "0.85rem", color: darkMode ? "#94a3b8" : "#6b7280" }}>{row.prev}</td>
                    <td style={{ padding: "10px 12px", textAlign: "center", fontSize: "0.85rem", fontWeight: 600,
                      color: row.change.startsWith("+") && row.metric === "Issues Detected" ? "#e53e3e"
                        : row.change.startsWith("-") && row.metric === "Issues Detected" ? "#38a169"
                        : row.change.startsWith("+") && row.metric === "Avg Response Time" ? "#e53e3e"
                        : row.change.startsWith("-") && row.metric === "Avg Response Time" ? "#38a169"
                        : row.change.startsWith("+") ? "#38a169" : row.change.startsWith("-") ? "#e53e3e"
                        : (darkMode ? "#94a3b8" : "#6b7280")
                    }}>{row.change}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── FOOTER ── */}
        <div style={{
          textAlign: "center", padding: "20px", fontSize: "0.8rem",
          color: darkMode ? "#64748b" : "#94a3b8", fontStyle: "italic"
        }}>
          This report was prepared by the MarTech Monitoring team based on continuous analysis of your
          Salesforce Marketing Cloud environment. For questions, contact support@martechmonitoring.com.
        </div>
      </div>
    </div>
  );
}

export default ReportDetail;
